import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronRight, Phone, Mail, Plus, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { ELECTRONICS_SUBCATEGORIES, CATEGORIES, BRAND, PTAC_PRODUCTS, PTAC_MISSING_MODELS, HOSPITALITY_TVS, HOSPITALITY_APPLIANCES, HOSPITALITY_ACCESSORIES } from "@/data/products";
import { useQuote } from "@/context/QuoteContext";

const stripHtml = (s = "") => s.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const CATALOGS = {
  "ptac-vtac": {
    products: PTAC_PRODUCTS,
    brands: ["Hotpoint by GE", "GE Zoneline", "Amana Distinctions", "GE TTW"],
    label: "PTAC / VTAC units",
    categoryName: "PTAC / VTAC Units",
  },
  "hospitality-tvs": {
    products: HOSPITALITY_TVS,
    brands: ["Samsung", "Philips"],
    label: "hospitality TVs",
    categoryName: "Hospitality TVs",
  },
  "hospitality-appliances": {
    products: HOSPITALITY_APPLIANCES,
    brands: ["Danby"],
    label: "hospitality appliances",
    categoryName: "Hospitality Appliances",
  },
  "hospitality-accessories": {
    products: HOSPITALITY_ACCESSORIES,
    brands: ["Coffee Makers", "Hair Dryers", "Irons"],
    label: "hospitality accessories",
    categoryName: "Hospitality Accessories",
  },
};

const ElectronicsSubCategoryPage = () => {
  const { sub } = useParams();
  const category = CATEGORIES.find((c) => c.slug === "electronics");
  const subcat = ELECTRONICS_SUBCATEGORIES.find((s) => s.slug === sub);
  const { addItem } = useQuote();

  useEffect(() => {
    if (subcat) {
      const prev = document.title;
      document.title = `${subcat.name} | Hospitality Hub`;
      return () => { document.title = prev; };
    }
  }, [subcat]);

  if (!subcat) {
    return (
      <main className="max-w-7xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif-display text-3xl">Sub-category not found</h1>
        <Link to="/products/electronics" className="text-[#D4AF37] underline mt-4 inline-block">Back to Electronics</Link>
      </main>
    );
  }

  const catalog = CATALOGS[sub];
  const hasCatalog = !!catalog;
  const products = hasCatalog ? catalog.products : [];

  const handleAdd = (p) => {
    addItem({ id: p.id, name: `${p.name} (${p.model})`, image: p.image }, catalog.categoryName);
    toast.success(`${p.model} added to your quote`);
  };

  return (
    <main data-testid={`electronics-sub-${sub}`}>
      {/* Hero */}
      <section className="relative">
        <div className="relative h-[46vh] min-h-[380px] md:h-[540px] w-full overflow-hidden">
          <img src={subcat.hero} alt={subcat.name} className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          <div className="absolute inset-0 flex items-end">
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full pb-14">
              <nav className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 mb-4">
                <Link to="/" className="hover:text-[#D4AF37]">Home</Link>
                <ChevronRight className="w-3 h-3" />
                <Link to="/products/electronics" className="hover:text-[#D4AF37]">Electronics</Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-[#D4AF37]">{subcat.name}</span>
              </nav>
              <h1 data-testid={`sub-title-${sub}`} className="font-serif-display text-5xl lg:text-7xl text-white max-w-3xl leading-tight">
                {subcat.name}
              </h1>
              <p className="text-white/90 mt-4 max-w-2xl text-base lg:text-lg">{subcat.blurb}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Catalog OR Coming Soon */}
      {hasCatalog ? (
        <>
          <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Catalog</p>
                <h2 className="font-serif-display text-3xl lg:text-4xl">Browse {catalog.label}</h2>
              </div>
              <p className="text-sm text-[#4A4A4A]">{products.length} products · Click <span className="text-[#D4AF37]">Add to Quote</span> for pricing.</p>
            </div>

            {/* Group by brand */}
            {catalog.brands.map((br) => {
              const list = products.filter((p) => p.brand === br);
              if (list.length === 0) return null;
              return (
                <div key={br} className="mb-14">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="h-px flex-1 bg-[#D4AF37]/30" />
                    <h3 data-testid={`brand-${br.toLowerCase().replace(/\s+/g,'-')}`} className="font-serif-display text-xl text-[#1A1A1A] whitespace-nowrap px-3">{br}</h3>
                    <span className="h-px flex-1 bg-[#D4AF37]/30" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
                    {list.map((p) => {
                      const productSlug = p.slug || p.model.toLowerCase().replace(/\//g, "-");
                      const detailHref = `/products/electronics/${sub}/${productSlug}`;
                      const cardKind = sub === 'hospitality-tvs' ? 'tv' : sub === 'hospitality-appliances' ? 'appliance' : sub === 'hospitality-accessories' ? 'accessory' : 'ptac';
                      return (
                      <article
                        key={p.id}
                        data-testid={`${cardKind}-card-${productSlug}`}
                        className="group bg-white border border-[#E5E5E5] hover:border-[#D4AF37]/60 hover:shadow-[0_12px_40px_rgb(0,0,0,0.06)] transition-all duration-500 flex flex-col"
                      >
                        <Link to={detailHref} data-testid={`card-image-link-${productSlug}`} className="block aspect-square w-full overflow-hidden bg-[#FAF9F6] flex items-center justify-center">
                          <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-700" />
                        </Link>
                        <div className="p-6 flex flex-col flex-1">
                          <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2">
                            {p.brand}
                            {p.pack ? <span className="ml-2 text-[#4A4A4A]">· {p.pack}</span> : <span> · Model {p.model}</span>}
                          </p>
                          <Link to={detailHref} data-testid={`card-name-link-${productSlug}`} className="font-serif-display text-lg leading-snug mb-2 hover:text-[#D4AF37] transition-colors">{p.name}</Link>
                          <p className="text-xs text-[#4A4A4A] leading-relaxed flex-1 line-clamp-3">{stripHtml(p.shortDescription).slice(0, 200)}{stripHtml(p.shortDescription).length > 200 ? '…' : ''}</p>
                          <div className="mt-5 grid grid-cols-1 gap-2">
                            <Link to={detailHref} data-testid={`view-details-${productSlug}`} className="w-full border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] py-3 text-xs uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2">View Details</Link>
                            <button
                              data-testid={`add-to-quote-${productSlug}`}
                              onClick={() => handleAdd(p)}
                              className="w-full bg-[#1A1A1A] hover:bg-[#D4AF37] text-white py-3 text-xs uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2"
                            >
                              <Plus className="w-3.5 h-3.5" /> Add to Quote
                            </button>
                          </div>
                        </div>
                      </article>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </section>

          {/* Sourcing note for missing models */}
          {PTAC_MISSING_MODELS.length > 0 && (
            <section className="bg-[#FAF9F6] border-y border-[#D4AF37]/20">
              <div className="max-w-7xl mx-auto px-6 md:px-12 py-14">
                <div className="flex items-start gap-3 mb-5">
                  <AlertCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-serif-display text-2xl">Sourcing in progress</h3>
                    <p className="text-sm text-[#4A4A4A] mt-1">
                      The following exact model numbers were requested but are not currently listed on our upstream distributor. Contact us to request pricing and availability directly from the manufacturer.
                    </p>
                  </div>
                </div>
                <ul data-testid="ptac-missing-list" className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm mt-4">
                  {PTAC_MISSING_MODELS.map((m) => (
                    <li key={m.model} data-testid={`missing-${m.model}`} className="flex items-baseline justify-between border-b border-[#E5E5E5] py-3">
                      <div>
                        <span className="font-serif-display text-base text-[#1A1A1A] mr-3">{m.model}</span>
                        <span className="text-xs text-[#4A4A4A]">{m.name}</span>
                      </div>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] whitespace-nowrap">{m.note}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/contact" data-testid="missing-request-quote" className="inline-flex items-center gap-2 mt-6 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-8 py-3 uppercase tracking-[0.2em] text-xs transition-colors">
                  Request Pricing on Missing Models
                </Link>
              </div>
            </section>
          )}
        </>
      ) : (
        <section className="max-w-4xl mx-auto px-6 md:px-12 py-24 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Catalog Launch Soon</p>
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-tight">
            {subcat.name}: full catalog arriving shortly.
          </h2>
          <p className="mt-6 text-[#4A4A4A] leading-relaxed text-lg">
            We're finalizing spec sheets and brand-approved photography for the {subcat.name.toLowerCase()} line.
            In the meantime, our team is already quoting active projects at factory-direct pricing. Reach out and we'll respond within 24 business hours.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="sub-cta-contact"
              className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-white px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Mail className="w-4 h-4" /> Request a Quote
            </Link>
            <a
              href={`tel:${BRAND.phone}`}
              data-testid="sub-cta-call"
              className="inline-flex items-center gap-2 border border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] px-10 py-4 uppercase tracking-[0.2em] text-xs transition-colors"
            >
              <Phone className="w-4 h-4" /> Call {BRAND.phone}
            </a>
          </div>
        </section>
      )}

      {/* Other electronics sub-categories */}
      <section className="bg-white border-t border-[#D4AF37]/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">Also in Electronics</p>
          <h3 className="font-serif-display text-3xl mb-8">Explore other electronics categories</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {ELECTRONICS_SUBCATEGORIES.filter((s) => s.slug !== sub).map((s) => (
              <Link
                key={s.slug}
                to={`/products/electronics/${s.slug}`}
                data-testid={`related-sub-${s.slug}`}
                className="group block border border-[#E5E5E5] hover:border-[#D4AF37]/60 bg-[#FAF9F6] overflow-hidden"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={s.hero} alt={s.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-5">
                  <p className="font-serif-display text-lg">{s.name}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default ElectronicsSubCategoryPage;
